// The icon buttons of an actions column (Edit, Deactivate, …): a ghost icon button with a tooltip, its
// name in `label` (aria-label and tooltip). Sized by the table density so compact rows stay compact.
//
//   { accessor: 'actions', title: t('…actions'), textAlign: 'center', width: 120, render: (r) => (
//     <DataTableRowActions>
//       <DataTableRowAction label={t('…edit')} onClick={() => edit(r)}><Pencil /></DataTableRowAction>
//       <DataTableRowAction label={t('…delete')} tone="danger" onClick={() => remove(r)}><Trash2 /></DataTableRowAction>
//     </DataTableRowActions>
//   ) }
//
// Tones (see ROW_ACTION_TONES): `danger`, `warn` and `ok` colour the icon and tint the hover; `info` and `ai` colour
// the icon and keep the ghost hover. A colour outside the set still goes in `className`.
import type * as React from 'react';
import { useContext } from 'react';

import { Button } from '@/components/lumen/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { cn } from '@/lib/utils';
import { DataTableDensityContext } from './mono';

const JUSTIFY = { left: 'justify-start', center: 'justify-center', right: 'justify-end' } as const;

/**
 * The icon colour (and hover) of each tone. The strings are the classes the list pages set through `className`
 * before the tones existed, so moving a page to `tone` renders the same class list.
 */
export const ROW_ACTION_TONES = {
  default: '',
  /** Destructive: delete, deactivate, cancel. */
  danger: 'text-err hover:bg-err/10 hover:text-err',
  /** Needs care: deactivate a bank, make a statement the default. */
  warn: 'text-warn hover:bg-warn/10 hover:text-warn',
  /** Positive: activate. */
  ok: 'text-ok hover:bg-ok/10 hover:text-ok',
  /** A primary, non-destructive action: view, resend, download, add, duplicate. */
  info: 'text-label hover:text-label',
  /** The violet plane: assign users. */
  ai: 'text-ai hover:text-ai',
} as const;

export type DataTableRowActionTone = keyof typeof ROW_ACTION_TONES;

export function DataTableRowActions({
  align = 'center',
  className,
  children,
}: {
  /** Match the column's `textAlign`. */
  align?: keyof typeof JUSTIFY;
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={cn('flex items-center gap-0.5', JUSTIFY[align], className)}>{children}</div>;
}

export interface DataTableRowActionProps
  extends Omit<React.ComponentProps<typeof Button>, 'variant' | 'size' | 'aria-label'> {
  /** The action's name: aria-label and tooltip. */
  label: string;
  /** The icon colour: `danger` for destructive actions (delete, deactivate); see ROW_ACTION_TONES. */
  tone?: DataTableRowActionTone;
  /** The icon. */
  children: React.ReactNode;
}

export function DataTableRowAction({
  label,
  tone = 'default',
  className,
  children,
  ...props
}: DataTableRowActionProps) {
  const density = useContext(DataTableDensityContext);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          className={cn(density === 'compact' && 'size-7', ROW_ACTION_TONES[tone], className)}
          {...props}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
